import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {openDialog} from 'platform/components';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useLazyGetVehicleQuery} from '@dms/api/sales';
import {isApiException} from '@dms/api/utils';
import {useLazyGetServiceOrderVariantMandatoryFieldsQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  ApiErrorOptions,
  handleApiError,
  queryParams,
  ORDER_TABS,
  MandatoryFieldsErrorModal,
  MandatoryFieldMapValue,
} from '@dms/shared';

import {composePath, isNotNilOrEmpty, useNavigate, useQueryState} from 'shared/web';

interface useHandleServiceOrderMandatoryFieldsErrorProps {
  orderId: string;
  serviceOrderVariantId?: string;
  vehicleId?: string;
}
type PathType = '/vehicles/:id/edit' | '/vehicles/:id/edit/:tab';

const VALIDATION_CODES = ['SVC-FE-ER-0118', 'SVC-FE-ER-0119'];
const VEHICLE_VALIDATION_CODES = ['SVC-FE-ER-0193'];

const VEHICLE_MANDATORY_FIELDS_MAP: Record<string, MandatoryFieldMapValue> = {
  vehicleRegistrationPlate: {
    title: i18n.t('entity.vehicle.labels.licensePlate'),
    href: vehiclesRoutes.editV2,
    formField: 'registrationPlate',
  },
  vehicleVin: {
    title: i18n.t('entity.vehicle.labels.vin'),
    href: vehiclesRoutes.editV2,
    formField: 'vin',
  },
  vehicleFirstRegistrationOn: {
    title: i18n.t('entity.vehicle.labels.firstRegistration'),
    href: vehiclesRoutes.editV2,
    formField: 'firstRegistrationOn',
  },
  vehicleWarrantyDate: {
    title: i18n.t('entity.vehicle.labels.warrantyUntil'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'warrantyDate',
  },
  vehicleTechnicalInspectionValidUntil: {
    title: i18n.t('entity.vehicle.labels.inspectionValidUntil'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'technicalInspectionValidUntil',
  },
};

export function useHandleServiceOrderMandatoryFieldsError(
  props: useHandleServiceOrderMandatoryFieldsErrorProps
) {
  const [, setOrderTab] = useQueryState(queryParams.SERVICE_CASE_ORDER_TAB);
  const [, setOrderId] = useQueryState(queryParams.SERVICE_CASE_ORDER_ID);
  const navigate = useNavigate();

  const [triggerMandatoryFields] = useLazyGetServiceOrderVariantMandatoryFieldsQuery();
  const [triggerVehicle] = useLazyGetVehicleQuery();

  const handleRedirect = (field: MandatoryFieldMapValue) => {
    const path = composePath(field.href as PathType, {
      params: {id: props.vehicleId, tab: isNotNilOrEmpty(field.tab) ? field.tab : undefined},
    });

    return navigate(path);
  };

  const handleMandatoryFieldsError = async (
    error: FetchBaseQueryError,
    stage?: 'BEFORE_OPENING' | 'BEFORE_CLOSING',
    options?: ApiErrorOptions & {skipQueryStateUpdate?: boolean}
  ) => {
    const errorData = error.data;

    if (
      isApiException(errorData) &&
      errorData.errors.some((error) => VALIDATION_CODES.includes(error.code)) &&
      options?.skipQueryStateUpdate !== true
    ) {
      setOrderTab(ORDER_TABS.DETAIL);
      setOrderId(props.orderId);
    }

    if (
      isApiException(errorData) &&
      errorData.errors.some((error) => VEHICLE_VALIDATION_CODES.includes(error.code))
    ) {
      const {serviceOrderVariantId, vehicleId} = props;

      if (isNilOrEmpty(serviceOrderVariantId) || isNilOrEmpty(vehicleId)) {
        return handleApiError(error, options);
      }

      const [mandatoryFields, vehicleData] = await Promise.all([
        triggerMandatoryFields({serviceOrderVariantId: serviceOrderVariantId ?? ''}, true)
          .unwrap()
          .catch(() => undefined),
        triggerVehicle({vehicleId: vehicleId ?? ''}, true)
          .unwrap()
          .catch(() => undefined),
      ]);

      if (isNil(mandatoryFields) || isNil(vehicleData)) {
        return handleApiError(error, options);
      }

      const isVehicleFieldFilled = (fieldKey: string) => {
        if (!vehicleData) {
          return false;
        }

        switch (fieldKey) {
          case 'vehicleRegistrationPlate':
            return isNotNilOrEmpty(vehicleData.state?.registrationPlate);
          case 'vehicleVin':
            return isNotNilOrEmpty(vehicleData.vin);
          case 'vehicleFirstRegistrationOn':
            return isNotNilOrEmpty(vehicleData.firstRegistrationOnYear);
          case 'vehicleWarrantyDate':
            return isNotNilOrEmpty(vehicleData.modelSpecification?.warrantyDate);
          case 'vehicleTechnicalInspectionValidUntil':
            return isNotNilOrEmpty(vehicleData.state?.technicalInspectionValidUntil);
          default:
            return false;
        }
      };

      const missingVehicleFields = (mandatoryFields ?? [])
        .filter(
          (field) =>
            field.field?.startsWith('vehicle') &&
            field.isSelected &&
            field.field &&
            !isVehicleFieldFilled(field.field)
        )
        .filter((field) => {
          if (stage) {
            return (
              field.mandatoryCondition?.find((condition) => condition.event === stage)
                ?.isSelected === true
            );
          }

          return true;
        })
        .reduce<MandatoryFieldMapValue[]>((accumulator, field) => {
          const mapValue = field.field ? VEHICLE_MANDATORY_FIELDS_MAP[field.field] : undefined;
          return mapValue ? [...accumulator, mapValue] : accumulator;
        }, []);

      const missingVehicleFieldLabels = missingVehicleFields.map((field) => field.title);

      return openDialog(
        <MandatoryFieldsErrorModal
          mandatoryFields={missingVehicleFields}
          translatedFields={missingVehicleFieldLabels}
          onSubmit={handleRedirect}
        />,
        {
          id: 'mandatory-fields-error-modal',
          title: i18n.t('entity.mandatoryFields.errorModal.title'),
        }
      );
    }

    handleApiError(error, options);
  };

  return handleMandatoryFieldsError;
}
